import React from "react";
import { CheckCircle2, TicketPercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { CouponOfferType, ICoupon, priceFormat } from "muminsmart-utils";

type CouponListDrawerProps = {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  coupons: ICoupon[];
  isLoading: boolean;
  isApplying: boolean;
  appliedCouponCode?: string;
  onApplyCoupon: (code: string) => void;
};

function getCouponDescription(coupon: ICoupon) {
  if (coupon.offerType === CouponOfferType.PercentageOffUpto) {
    const percentage = coupon.percentageOff ?? 0;
    const maxDiscount = coupon.maxDiscountAmount
      ? ` upto ${priceFormat(coupon.maxDiscountAmount)}`
      : "";

    return `${percentage}% off${maxDiscount}`;
  }

  if (coupon.offerType === CouponOfferType.BuyXGetYFree) {
    return `Buy ${coupon.buyQuantity ?? 0} get ${coupon.freeQuantity ?? 0} free`;
  }

  return `${priceFormat(coupon.flatDiscountAmount ?? 0)} off on min order ${priceFormat(coupon.minOrderAmount ?? 0)}`;
}

export default function CouponListDrawer({
  open,
  onOpenChange,
  coupons,
  isLoading,
  isApplying,
  appliedCouponCode,
  onApplyCoupon,
}: CouponListDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <button
          className="w-full mx-auto mt-4 py-2 text-sm font-medium text-primary underline underline-offset-3 bg-[#FAF4EA] rounded-md"
          type="button"
        >
          View All Coupons
        </button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mx-auto w-full max-w-2xl">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">
              <TicketPercent className="h-5 w-5" />
              Available Coupons
            </DrawerTitle>
            <DrawerDescription>
              Pick a coupon to apply it instantly on this order.
            </DrawerDescription>
          </DrawerHeader>

          <div className="max-h-[60dvh] space-y-2 overflow-y-auto px-3 py-4 sm:px-4">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">
                Loading coupons...
              </p>
            ) : coupons.length ? (
              coupons.map((coupon) => {
                const isApplied = appliedCouponCode === coupon.code;

                return (
                  <div
                    key={coupon.id}
                    className={`flex items-center justify-between gap-3 rounded-xl border p-3 sm:p-4 ${
                      isApplied
                        ? "border-primary bg-primary/5"
                        : "border-zinc-200"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-zinc-900">
                          {coupon.code}
                        </p>
                        {isApplied && (
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                        )}
                      </div>
                      <p className="truncate text-sm text-zinc-800">
                        {coupon.name}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {getCouponDescription(coupon)}
                      </p>
                    </div>

                    <Button
                      type="button"
                      variant={isApplied ? "secondary" : "default"}
                      size="sm"
                      className="shrink-0"
                      disabled={isApplying || isApplied}
                      onClick={() => {
                        onApplyCoupon(coupon.code);
                      }}
                    >
                      {isApplied ? "Applied" : "Apply"}
                    </Button>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">
                No valid coupons available right now.
              </p>
            )}
          </div>

          <DrawerFooter>
            <DrawerClose asChild>
              <Button className="rounded-full" variant="secondary">
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
